import { closeModalFunction, URL, closeModalIconTestScoreGuidance, closeModalIcon } from "../dictionary"
import axios from "axios"
import { getAuth, useAuth } from "../../modules/auth"
import { TestScoreStrategyReactGAHandlers } from "../ReactGAHandlers"
import { handleUpdateUserProfile, handleUpdateUserData } from "../../pages/UserProfile"
import { useState } from "react"
import * as Yup from 'yup'
import clsx from 'clsx'
import { useFormik } from 'formik'

let apiToken: any = undefined

if (getAuth()?.api_token && getAuth()?.groups) {
  apiToken = getAuth()?.api_token
}

const EditUserProfileModal = ({ user, setUser }) => {

  const [loading, setLoading] = useState(false)
  const classRankNumberMin = 1
  const classRankSizeMin = 1

  const isEmpty = (val: any) => val === '' || val === null || val === undefined;

  const formSchema = Yup.object().shape({
    satVerbal: Yup.number()
      .nullable()
      .min(200, 'Score cannot be lower than 200')
      .max(800, 'Score cannot exceed 800'),
    satMath: Yup.number()
      .nullable()
      .min(200, 'Score cannot be lower than 200')
      .max(800, 'Score cannot exceed 800'),
    actComposite: Yup.number()
      .nullable()
      .min(1, 'Score cannot be lower than 1')
      .max(36, 'Score cannot exceed 36'),
    gpaUnweighted: Yup.number()
      .nullable()
      .min(0, 'GPA based on a 4.0 scale (enter unweighted value between 0.00 - 4.00)')
      .max(4.0, 'GPA based on a 4.0 scale (enter unweighted value between 0.00 - 4.00)').test(
        'max-2-decimals',
        'GPA based on a 4.0 scale (enter unweighted value between 0.00 - 4.00). GPA must not exceed two decimal points.',
        (value) => {
          if (value === null || value === undefined) return true;
          return /^\d+(\.\d{1,2})?$/.test(String(value));
        }
      ),
    gpaWeighted: Yup.number()
      .nullable()
      .min(0, 'GPA based on a 4.0 scale (enter weighted value between 0.00 - 5.00)')
      .max(5.0, 'GPA based on a 4.0 scale (enter weighted value between 0.00 - 5.00)').test(
        'max-2-decimals',
        'GPA based on a 4.0 scale (enter weighted value between 0.00 - 5.00). GPA must not exceed two decimal points.',
        (value) => {
          if (value === null || value === undefined) return true;
          return /^\d+(\.\d{1,2})?$/.test(String(value));
        }
      ),
    // classRankNumber: Yup.number().transform(value => (value === '' ? null : value)).nullable().min(classRankNumberMin, '').test(
    //   'require-classRanknumber-if-classRankSize',
    //   'Class rank must also be entered.',
    //   function (value) {
    //     const { classRankSize } = this.parent;
    //     const hasSize = classRankSize !== null && classRankSize !== undefined;
    //     const hasNumber = value !== null && value !== undefined;
    //     if (hasSize && !hasNumber) {
    //       return false;
    //     }
    //     return true;
    //   }
    // ),
    classRankNumber: Yup.number().transform((value, originalValue) => (originalValue === '' ? null : value)).typeError('Class Rank value must be a number')
      .nullable()
      .min(classRankNumberMin, 'Class Rank must be greater than 0')
      .test(
        'require-classRankNumber-if-classRankSize',
        'Class rank must also be entered.',
        function (value) {
          const { classRankSize } = this.parent;
          const hasSize = classRankSize !== null && classRankSize !== undefined && classRankSize !== '';
          const hasNumber = value !== null && value !== undefined;
          return !(hasSize && !hasNumber);
        }
      )
      .test(
        'rank-not-greater-than-size',
        'Class rank must not be greater than class size.',
        function (value) {
          const { classRankSize } = this.parent;
          const hasSize = classRankSize !== null && classRankSize !== undefined && classRankSize !== '';
          const hasNumber = value !== null && value !== undefined;
          if (hasSize && hasNumber) {
            return value <= classRankSize;
          }
          return true;
        }
      ),
    // classRankSize: Yup.number()
    //   .nullable()
    //   .min(classRankSizeMin, '')
    //   .test(
    //     'require-classRankSize-if-classRankNumber',
    //     'Class size must also be entered.',
    //     function (value) {
    //       const { classRankNumber } = this.parent;
    //       const hasNumber = !isEmpty(classRankNumber);
    //       const hasSize = !isEmpty(value);
    //       return !(hasNumber && !hasSize);
    //     }
    //   ),
    classRankSize: Yup.number().transform((value, originalValue) => (originalValue === '' ? null : value)).typeError('Class Size value must be a number')
      .nullable()
      .min(classRankSizeMin, 'Class Size must be greater than 0')
      .test(
        'require-classRankSize-if-classRankNumber',
        'Class size must also be entered.',
        function (value) {
          const { classRankNumber } = this.parent;
          const hasNumber = classRankNumber !== null && classRankNumber !== undefined && classRankNumber !== '';
          const hasSize = value !== null && value !== undefined;
          return !(hasNumber && !hasSize);
        }
      ),
    classRankTier: Yup.number().nullable(),
  })

  let initialValues = {
    satVerbal: user?.satVerbal,
    satMath: user?.satMath,
    actComposite: user?.actComposite,
    gpaUnweighted: user?.gpaUnweighted,
    gpaWeighted: user?.gpaWeighted,
    classRankNumber: user?.classRankNumber,
    classRankSize: user?.classRankSize,
    classRankTier: user?.classRankTier,
  }

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: formSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true)

      const cleanedValues = Object.fromEntries(
        Object.entries(values).map(([key, value]) => [key, value === "" ? null : value])
      );

      if (cleanedValues.satVerbal == null && cleanedValues.satMath == null) {
        cleanedValues.satComposite = null
      } else {
        cleanedValues.satComposite = cleanedValues.satVerbal + cleanedValues.satMath
      }

      try {
        await handleUpdateUserData(user, cleanedValues, setUser, apiToken);
        await handleUpdateUserProfile(setUser, apiToken)
      } catch (error) {
        console.error(error)
        alert('Error encountered')
        setStatus('The test score details are incorrect')
        setSubmitting(false)
      } finally {
        setLoading(false)
      }
    }
  })

  const {
    handleGAEventEditScoreProfile,
    handleGAEventOverlayButton
  } = TestScoreStrategyReactGAHandlers()

  const returnInputBlock = ({ title, name, min, max, act2 = false }) => {
    const isGPAField = name === 'gpaUnweighted' || name === 'gpaWeighted';
    const isClassRankField = name === 'classRankSize' || name === 'classRankNumber';

    return (
      <div className="d-flex flex-column mb-3">
        <label className={`form-label dca-testscore-editscore-field-title${act2 ? 2 : ''}`}>
          {title}
        </label>
        <div className="d-flex flex-row">
          <input
            type="number"
            min={min}
            {...(max != null ? { max } : {})}
            {...formik.getFieldProps(name)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const raw = e.target.value;

              if (isGPAField) {
                const parsed = parseFloat(raw);
                if (!isNaN(parsed) || raw === '') {
                  formik.setFieldValue(name, raw);
                }
              } else {
                if (raw === '') {
                  formik.setFieldValue(name, null);
                } else if (!isNaN(Number(raw))) {
                  formik.setFieldValue(name, Number(raw));
                }
              }
            }}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
              const raw = e.target.value;

              if (isGPAField) {
                const parsed = parseFloat(raw);
                if (!isNaN(parsed)) {
                  formik.setFieldValue(name, parsed.toFixed(2));
                }
              } else if (isClassRankField) {
                if (raw === '') {
                  formik.setFieldValue(name, null);
                } else {
                  const parsed = parseInt(raw, 10);
                  if (!isNaN(parsed)) {
                    formik.setFieldValue(name, parsed);
                  }
                }

                if (name === 'classRankNumber' && !formik.touched.classRankSize) {
                  formik.setFieldTouched('classRankSize', true, true);
                }
                if (name === 'classRankSize' && !formik.touched.classRankNumber) {
                  formik.setFieldTouched('classRankNumber', true, true);
                }
              }

              formik.handleBlur(e);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const raw = e.currentTarget.value;
                if (isGPAField) {
                  const parsed = parseFloat(raw);
                  if (!isNaN(parsed)) {
                    formik.setFieldValue(name, parsed.toFixed(2));
                  }
                } else {
                  if (raw === '') {
                    formik.setFieldValue(name, null);
                  } else {
                    const parsed = parseInt(raw, 10);
                    if (!isNaN(parsed)) {
                      formik.setFieldValue(name, parsed);
                    }
                  }
                }
                e.currentTarget.blur();
              }
            }}
            className={clsx(
              "form-control dca-testscore-editscore-input",
              {
                'is-invalid': formik.touched[name] && formik.errors[name],
                'is-valid': formik.touched[name] && !formik.errors[name],
              }
            )}
          />
          <i
            className="bi bi-x-circle align-self-center"
            style={{ cursor: 'pointer', marginLeft: '5px' }}
            // onClick={() => {
            //   formik.setFieldValue(name, '', true);
            //   formik.setFieldTouched(name, false, false);

            //   formik.validateField('classRankNumber');
            //   formik.validateField('classRankSize');

            //   setTimeout(() => {
            //     const rank = formik.values.classRankNumber;
            //     const size = formik.values.classRankSize;

            //     if (name === 'classRankNumber' && (size === null || size === '')) {
            //       formik.setFieldTouched('classRankSize', false, false);
            //     }

            //     if (name === 'classRankSize' && (rank === null || rank === '')) {
            //       formik.setFieldTouched('classRankNumber', false, false);
            //     }
            //   }, 0);
            // }}
            onClick={() => {
              formik.setFieldValue(name, '', true);
              if (name === 'classRankNumber' || name === 'classRankSize') {
                const rank = formik.values.classRankNumber;
                const size = formik.values.classRankSize;

                const rankIsValid = rank !== null && rank !== '';
                const sizeIsValid = size !== null && size !== '';

                if (name === 'classRankSize' && !rankIsValid) {
                  formik.setFieldTouched(name, false, false);
                  formik.setFieldTouched('classRankNumber', false, false);
                } else if (name === 'classRankNumber' && !sizeIsValid) {
                  formik.setFieldTouched(name, false, false);
                  formik.setFieldTouched('classRankSize', false, false);
                } else {
                  formik.setFieldTouched(name, true, false);
                }
              } else {
                formik.setFieldTouched(name, false, false);
              }

              setTimeout(() => {
                const rank = formik.values.classRankNumber;
                const size = formik.values.classRankSize;

                const rankIsValid = rank !== null && rank !== '';
                const sizeIsValid = size !== null && size !== '';

                if (name === 'classRankSize') {
                  if (rankIsValid && !sizeIsValid) {
                    formik.setFieldTouched('classRankSize', true, false);
                    formik.validateField('classRankSize');
                  } else if (!rankIsValid && !sizeIsValid) {
                    formik.setFieldTouched('classRankSize', false, false);
                    formik.setFieldTouched('classRankNumber', false, false);
                  }
                }

                if (name === 'classRankNumber') {
                  if (sizeIsValid && !rankIsValid) {
                    formik.setFieldTouched('classRankNumber', true, false);
                    formik.validateField('classRankNumber');
                  } else if (!rankIsValid && !sizeIsValid) {
                    formik.setFieldTouched('classRankSize', false, false);
                    formik.setFieldTouched('classRankNumber', false, false);
                  }
                }
              }, 0);
            }}
          />
        </div>
        {formik.touched[name] && formik.errors[name] && (
          <div className="invalid-feedback d-flex dca-editscore-modal-error-text">
            {formik.errors[name]}
          </div>
        )}
      </div>
    );
  };


  const gpa = (
    <div>
      <p className='dca-testscore-editscore-sat-section-title'>Grade Point Average (GPA)</p>
      <div className="dca-editscoreprofile-modal-score-wrapper">
        {returnInputBlock({ title: 'Unweighted GPA', name: 'gpaUnweighted', min: 0, max: 4.0 })}

        {returnInputBlock({ title: 'Weighted GPA', name: 'gpaWeighted', min: 0, max: 5.0 })}
      </div>

    </div>
  )

  const satScores = (
    <div>
      <p className='dca-testscore-editscore-sat-section-title'>SAT Scores</p>
      <div className="dca-editscoreprofile-modal-score-wrapper">
        {returnInputBlock({ title: 'Best SAT Reading Score', name: 'satVerbal', min: 200, max: 800 })}

        {returnInputBlock({ title: 'Best SAT Math Score', name: 'satMath', min: 200, max: 800 })}
      </div>

    </div>
  )

  const actScores = (
    <div>
      <p className='dca-testscore-editscore-sat-section-title'>ACT Score</p>

      <div className="dca-editscoreprofile-modal-score-wrapper">
        {returnInputBlock({ title: 'Best ACT Composite Score', name: 'actComposite', min: 1, max: 36 })}
      </div>
    </div>
  )

  const classRankTierField = (
    <div className="d-flex flex-column mb-3">
      <label className={`form-label dca-testscore-editscore-field-title`}>
        Class Rank Tier
      </label>
      <div className="d-flex flex-row">
        <select
          className={clsx(
            "form-select dca-testscore-editscore-input",
            {
              'is-invalid': formik.touched['classRankTier'] && formik.errors['classRankTier'],
              'is-valid': formik.touched['classRankTier'] && !formik.errors['classRankTier'],
            }
          )}
          // className='form-select'
          // data-control="select2"
          {...formik.getFieldProps('classRankTier')}
          name="classRankTier"
        // style={inputStyle}
        >
          <option value={undefined} hidden>Select Class Rank Tier</option>
          <option value={1}>Top 1%</option>
          <option value={2}>Top 5%</option>
          <option value={3}>Top 10%</option>
          <option value={4}>Top 25%</option>
          <option value={5}>Top 50%</option>
          <option value={6}>Bottom 50%</option>
        </select>
        <i
          className="bi bi-x-circle align-self-center"
          style={{ cursor: 'pointer', marginLeft: '5px' }}
          onClick={() => {
            formik.setFieldValue('classRankTier', "");
            formik.setFieldTouched('classRankTier', false)
          }}
        />
      </div>
      {/* {formik.touched['classRankTier'] && formik.errors['classRankTier'] && (
        <div className="invalid-feedback d-flex dca-editscore-modal-error-text">
          {formik.errors['classRankTier']}
        </div>
      )} */}
    </div>
  )

  const classRankScores = (
    <div>
      <p className='dca-testscore-editscore-sat-section-title'>Class Rank</p>

      <div className="dca-editscoreprofile-modal-score-wrapper">
        {returnInputBlock({ title: 'Class Rank', name: 'classRankNumber', min: classRankNumberMin, max: null })}

        {returnInputBlock({ title: 'Class Size', name: 'classRankSize', min: classRankSizeMin, max: null })}
      </div>

      <div className="dca-editscoreprofile-modal-score-wrapper">
        {classRankTierField}
      </div>
    </div>
  )

  return (<div className="modal fade" id="editUserProfile">
    <div className="modal-dialog modal-dialog modal-dialog-centered">
      <div className="modal-content dca-editscore-profile-modal-scattergram">

        <div className="modal-body fw-bold d-flex flex-column">
          <p className="dca-testscore-editscore-title">Enter Your Profile Details</p>

          <span className="form-label dca-edit-user-profile-modal-message">Unweighted GPA or class rank required for personalized fit assessments. For best results, please provide unweighted GPA, class rank (exact rank if available, otherwise indicate the estimated tier), and SAT/ACT test scores.</span>

          <form
            onSubmit={formik.handleSubmit}
            noValidate
            id="testscoresForm">
            {/* {closeModalIconTestScoreGuidance(handleGAEventOverlayButton)} */}
            {closeModalIcon('editscore-user-profile')}

            <div className='d-flex flex-column justify-content-center' style={{ width: '100%' }}>
              {gpa}
              {classRankScores}
              {satScores}
              {actScores}
            </div>

            {/* Original */}
            <div className="d-flex flex-row justify-content-center" style={{ marginTop: '45px' }}>
              <button
                type="button"
                className="btn btn-light dca-testscore-editscore-discard-bttn"
                data-bs-dismiss="modal"
                id="closeModalIcon"
                // onClick={() => handleGAEventOverlayButton("Discard")}
                style={{ marginRight: '12px' }}
              >
                Discard
              </button>
              <button
                type="submit"
                className="rounded dca-testscore-editscore-submit-bttn"
                disabled={!formik.isValid || formik.isSubmitting}
                data-bs-dismiss="modal"
                style={{ marginLeft: '12px' }}
              >
                Submit
              </button>
            </div>

          </form>
        </div>

      </div>
    </div>
  </div>)
}

export { EditUserProfileModal }